/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useLayoutEffect, useState } from 'react';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { createEndAIExperienceCommand } from '../../../pm-plugins/decoration/actions';
import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import { hasGeneratedContentDecorations } from '../../../pm-plugins/decoration/utils/hasGeneratedContentDecorations';
import type { EndExperience } from '../../../types/types';
import {
	ContentComponentContextProvider,
	useContentComponentContext,
} from '../../hooks/useContentComponentContext';
import type { ContentComponentProps } from '../../hooks/useContentComponentContext';
import { getAIHighlightPositions } from '../../utils/selection';
import { AgentFetcher } from '../AgentFetcher/AgentFetcher';
import { GlobalWrapper } from '../GlobalWrapper/GlobalWrapper';
import { PublishToRovo } from '../PublishToRovo/PublishToRovo';
import { RovoEnabled } from '../RovoEnabled/RovoEnabled';
import { SessionUIComponent } from '../SessionUI/SessionUIComponent';
import { SessionUIComponent as SessionUIComponentLegacy } from '../SessionUI/SessionUIComponentLegacy';
import { SubscribeToRovo } from '../SubscribeToRovo/SubscribeToRovo';

import { ContentComponent } from './ContentComponent';

const useSharedPluginStateNoDebounce = (api: ContentComponentProps['api']) => {
	const [state, setState] = useState(
		api?.aiExperience?.sharedState.currentState()?.aiDecorationExperiencePluginState
			.modalMountedTimeStamp,
	);
	useLayoutEffect(() => {
		const unsub = api?.aiExperience?.sharedState.onChange(
			({ prevSharedState, nextSharedState }) => {
				if (
					prevSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp !==
					nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp
				) {
					setState(nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp);
				}
			},
		);

		return () => {
			unsub?.();
		};
	}, [api]);

	return state;
};

const SubscribeToRovoWithPositions = () => {
	const { api, editorView, editorPluginAIProvider } = useContentComponentContext();
	const { modalDecorationSet } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => ({
			modalDecorationSet:
				state.aiExperienceState?.aiDecorationExperiencePluginState.modalDecorationSet,
		}),
	);
	const positions = getAIHighlightPositions({ modalDecorationSet });

	return (
		<SubscribeToRovo
			editorApi={api}
			positions={positions}
			editorView={editorView}
			product={editorPluginAIProvider.product}
			onDocChangeByAgent={editorPluginAIProvider.onDocChangeByAgent}
			onAIProviderChanged={editorPluginAIProvider.onAIProviderChanged}
			editorPluginAIProvider={editorPluginAIProvider}
		/>
	);
};

export const ContentComponentWrapper = (
	props: Omit<
		ContentComponentProps,
		| 'isRovoLoading'
		| 'setIsRovoLoading'
		| 'cancelRovoPrompt'
		| 'setCancelRovoPrompt'
		| 'endExperience'
	>,
): JSX.Element => {
	const modalMountedTimeStamp = useSharedPluginStateNoDebounce(props.api);
	const { targetId, isDisplayingChanges, isRunning, editorViewModeMode } =
		useSharedPluginStateWithSelector(
			props.api,
			['aiExperience', 'trackChanges', 'editorViewMode'],
			(state) => ({
				targetId: state.aiExperienceState?.aiDecorationExperiencePluginState.targetId,
				isDisplayingChanges: state.trackChangesState?.isDisplayingChanges,
				isRunning: state.aiExperienceState?.aiDecorationExperiencePluginState.isRunning,
				editorViewModeMode: state.editorViewModeState?.mode,
			}),
		);
	const isViewMode = editorViewModeMode === 'view';

	const endExperience: EndExperience = useCallback(
		(options) => {
			// after AI start, there is a chance that the plugin state is not updated yet
			// so we need to check if the plugin state is running before ending the experience
			if (!isRunning) {
				return;
			}

			const state = getPluginState(props.editorView.state);

			const decoSet =
				hasGeneratedContentDecorations(state) || options?.preserveEditorSelectionOnComplete
					? state.modalDecorationSet
					: undefined;

			const endAIExperienceCommand = createEndAIExperienceCommand(
				decoSet,
				options?.preserveEditorSelectionOnComplete,
				options?.shouldClearSelectionContext,
				options?.shouldIgnoreNextSpaceTrigger,
			);
			endAIExperienceCommand(props.editorView.state, props.editorView.dispatch);
		},
		[props.editorView, isRunning],
	);

	/**
	 * This is to detect the state of the Rovo prompt and loading screens
	 * from RovoInlineChatPrompt used in RovoPromptScreenWithLogic,
	 * as it does not respect the state value from XState.
	 */
	const [isRovoLoading, setIsRovoLoading] = useState(false);
	/**
	 * Stores Rovo's cancel function from onSubmit callback so ExperienceApplication's
	 * ESC handler can call it. Needed because editor loses focus when disabled during
	 * streaming, breaking normal ESC handling.
	 */
	const [cancelRovoPrompt, setCancelRovoPrompt] = useState<() => void>();

	/* reset the component when start AI again, so that the error boundary will also be reset */
	return (
		<GlobalWrapper>
			<ContentComponentContextProvider
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				value={{
					...props,
					isRovoLoading,
					setIsRovoLoading,
					targetId,
					isDisplayingChanges,
					setCancelRovoPrompt,
					cancelRovoPrompt,
					endExperience,
				}}
			>
				{!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true) && (
					<>
						<ContentComponent key={modalMountedTimeStamp} />
						<SessionUIComponentLegacy
							api={props.api}
							providerFactory={props.providerFactory}
							appearance={props.appearance}
						/>
					</>
				)}

				{/* eslint-disable-next-line @atlaskit/platform/no-preconditioning */}
				{fg('platform_editor_ai_adf_prompts_in_all_products') &&
					expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true) && (
						<>
							{!fg('platform_editor_ai_remove_agents_list') && (
								<RovoEnabled
									editorView={props.editorView}
									editorPluginAIProvider={props.editorPluginAIProvider}
								>
									<PublishToRovo
										editorView={props.editorView}
										getMentionNameDetails={props.getMentionNameDetails}
										editorPluginAIProvider={props.editorPluginAIProvider}
										editorApi={props.api}
										isViewMode={isViewMode}
									/>
									<AgentFetcher
										editorPluginAIProvider={props.editorPluginAIProvider}
										editorView={props.editorView}
									/>
									<SubscribeToRovoWithPositions />
								</RovoEnabled>
							)}
							<SessionUIComponent
								api={props.api}
								providerFactory={props.providerFactory}
								appearance={props.appearance}
							/>
						</>
					)}
			</ContentComponentContextProvider>
		</GlobalWrapper>
	);
};
